import React from 'react'
import {
  Box,
  SimpleGrid,
  Text,
  Stack,
  Container,
  Heading,
  useColorModeValue,
  Icon,
  Flex
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon } from '@chakra-ui/icons'
import { FaBrain, FaRocket, FaUserClock } from 'react-icons/fa'

const MotionBox = motion(Box)

interface WhyCardProps {
  title: string
  desc: string
  icon: React.ElementType
  benefits: string[]
  delay: number
}

const WhyCard = ({ title, desc, icon, benefits, delay }: WhyCardProps) => {
  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      bg="dark.card"
      p={8}
      borderRadius="2xl"
      border="1px solid"
      borderColor="whiteAlpha.100"
      _hover={{
        borderColor: 'brand.500',
        transform: 'translateY(-5px)'
      }}
      transitionProperty="transform, border-color"
      transitionDuration="0.3s"
    >
      <Flex
        w={12}
        h={12}
        align="center"
        justify="center"
        bg="whiteAlpha.100"
        rounded="xl"
        color="brand.400"
        mb={6}
      >
        <Icon as={icon} w={6} h={6} />
      </Flex>

      <Heading size="md" color="white" mb={3}>
        {title}
      </Heading>
      <Text color="gray.400" mb={6} lineHeight="relaxed">
        {desc}
      </Text>

      <Stack spacing={3}>
        {benefits.map((benefit, idx) => (
          <Flex key={idx} align="center">
            <CheckCircleIcon color="green.500" mr={3} w={4} h={4} />
            <Text color="gray.300" fontSize="sm">
              {benefit}
            </Text>
          </Flex>
        ))}
      </Stack>
    </MotionBox>
  )
}

const WhySection = () => {
  const { t } = useTranslation()
  const gridColor = useColorModeValue('#eceff7', '#1f2430')

  const items = [
    {
      title: t('감정 배제'),
      desc: t('알고리즘 신호에 따라 일관된 실행으로 일시적 공포와 탐욕에서 자유로워집니다.'),
      icon: FaBrain,
      benefits: [t('사전에 정의한 진입/청산 규칙'), t('리스크/포지션 사이징 자동화')]
    },
    {
      title: t('학습 곡선 단축'),
      desc: t('검증된 리소스와 커뮤니티 자료로 빠르게 전략을 이해하고 적용할 수 있습니다.'),
      icon: FaRocket,
      benefits: [t('트레이딩뷰 스크립트 연동 가이드'), t('XRP 특화 인사이트 영상')]
    },
    {
      title: t('즉시 시작'),
      desc: t('회원가입 후 API 연결만 하면 복잡한 설정 없이 자동으로 운용이 시작됩니다.'),
      icon: FaUserClock,
      benefits: [t('5분 만에 셋업 완료'), t('백테스트 프리셋 제공(옵션)')]
    }
  ]

  return (
    <Box position="relative" py={{ base: 20, md: 24 }} bg="dark.bg" overflow="hidden">
      {/* Background Grid */}
      <Box
        position="absolute"
        inset={0}
        bgImage={`linear-gradient(${gridColor} 1px, transparent 1px), linear-gradient(90deg, ${gridColor} 1px, transparent 1px)`}
        bgSize="40px 40px"
        opacity={0.2}
        pointerEvents="none"
      />
      
      {/* Radial Gradient Overlay */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        w="100%"
        h="100%"
        bgGradient="radial(circle at center, transparent 0%, dark.bg 100%)"
        pointerEvents="none"
      />

      <Container maxW="6xl" position="relative" zIndex={1}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          {items.map((item, index) => (
            <WhyCard
              key={index}
              title={item.title}
              desc={item.desc}
              icon={item.icon}
              benefits={item.benefits}
              delay={index * 0.1}
            />
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  )
}

export default WhySection
